import { useT } from "../../shared/i18n/i18n";
import { emojis } from "@tiptap/extension-emoji";
import {
  Car,
  Clock,
  Flag,
  Hand,
  Hash,
  Leaf,
  Lightbulb,
  Pizza,
  Search,
  Smile,
  Trophy,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import type { ComponentType } from "react";
import { cn } from "../lib/cn";

export const quickReactionEmoji = ["👍", "❤️", "🔥", "😂", "🎉", "👀", "✅"];

type EmojiEntry = { char: string; name: string; search: string };
type EmojiCategory = { entries: EmojiEntry[]; icon: ComponentType<{ className?: string }>; key: string; labelKey: string };

const categoryMeta: Array<{ group: string; icon: ComponentType<{ className?: string }>; key: string; labelKey: string }> = [
  { group: "", icon: Smile, key: "smileys", labelKey: "emoji.smileys" },
  { group: "people & body", icon: Hand, key: "people", labelKey: "emoji.people" },
  { group: "animals & nature", icon: Leaf, key: "nature", labelKey: "emoji.animals" },
  { group: "food & drink", icon: Pizza, key: "food", labelKey: "emoji.food" },
  { group: "travel & places", icon: Car, key: "travel", labelKey: "emoji.travel" },
  { group: "activities", icon: Trophy, key: "activities", labelKey: "emoji.activities" },
  { group: "objects", icon: Lightbulb, key: "objects", labelKey: "emoji.objects" },
  { group: "symbols", icon: Hash, key: "symbols", labelKey: "emoji.symbols" },
  { group: "flags", icon: Flag, key: "flags", labelKey: "emoji.flags" },
];

const emojiCategories: EmojiCategory[] = categoryMeta.map((meta) => ({
  entries: emojis.flatMap((entry) => {
    if (entry.group !== meta.group || !entry.emoji || entry.name.startsWith("regional_indicator")) return [];
    return [
      {
        char: entry.emoji,
        name: entry.name,
        search: [entry.name, ...entry.shortcodes, ...(entry.tags ?? [])].join(" ").toLowerCase(),
      },
    ];
  }),
  icon: meta.icon,
  key: meta.key,
  labelKey: meta.labelKey,
}));

const allEntries = emojiCategories.flatMap((category) => category.entries);

const recentsStorageKey = "impacto.emoji-recents";
const maxRecents = 24;

function readRecents(): string[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(recentsStorageKey) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string").slice(0, maxRecents) : [];
  } catch {
    return [];
  }
}

function pushRecent(char: string) {
  const next = [char, ...readRecents().filter((entry) => entry !== char)].slice(0, maxRecents);
  try {
    window.localStorage.setItem(recentsStorageKey, JSON.stringify(next));
  } catch {
    // Storage full or unavailable — recents are best-effort.
  }
}

type EmojiPickerProps = {
  autoFocusSearch?: boolean;
  className?: string;
  onPick: (emoji: string) => void;
};

export function EmojiPicker({ autoFocusSearch = true, className, onPick }: EmojiPickerProps) {
  const t = useT();
  const [query, setQuery] = useState("");
  const [activeCategoryKey, setActiveCategoryKey] = useState<string | null>(null);
  const [recents] = useState(readRecents);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const normalizedQuery = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!normalizedQuery) return [];
    const startsWith: EmojiEntry[] = [];
    const includes: EmojiEntry[] = [];
    for (const entry of allEntries) {
      if (entry.name.startsWith(normalizedQuery)) {
        startsWith.push(entry);
      } else if (entry.search.includes(normalizedQuery)) {
        includes.push(entry);
      }
      if (startsWith.length >= 160) break;
    }
    return [...startsWith, ...includes].slice(0, 160);
  }, [normalizedQuery]);

  const sections: Array<{ entries: EmojiEntry[]; icon: ComponentType<{ className?: string }>; key: string; labelKey: string }> = [
    ...(recents.length > 0
      ? [{ entries: recents.map((char) => ({ char, name: char, search: "" })), icon: Clock, key: "recent", labelKey: "emoji.recent" }]
      : []),
    ...emojiCategories,
  ];
  const activeKey = activeCategoryKey ?? sections[0]?.key;

  function pick(char: string) {
    pushRecent(char);
    onPick(char);
  }

  function scrollToSection(key: string) {
    const container = scrollRef.current;
    const section = container?.querySelector<HTMLElement>(`[data-emoji-section="${key}"]`);
    if (!container || !section) return;
    setActiveCategoryKey(key);
    container.scrollTo({ top: section.offsetTop });
  }

  function syncActiveCategory() {
    const container = scrollRef.current;
    if (!container) return;
    let current: string | null = null;
    for (const section of container.querySelectorAll<HTMLElement>("[data-emoji-section]")) {
      if (section.offsetTop <= container.scrollTop + 48) {
        current = section.dataset.emojiSection ?? null;
      }
    }
    if (current) setActiveCategoryKey(current);
  }

  return (
    <div className={cn("flex h-80 w-72 flex-col", className)}>
      <div className="relative p-2 pb-1.5">
        <Search className="pointer-events-none absolute left-4 top-1/2 size-3.5 -translate-y-[calc(50%+1px)] text-muted-foreground" />
        <input
          autoFocus={autoFocusSearch}
          className="h-8 w-full rounded-md bg-muted/50 pl-7 pr-2 text-sm outline-none transition-colors placeholder:text-muted-foreground/70 focus:bg-muted/70"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={t("emoji.search")}
          value={query}
        />
      </div>

      {normalizedQuery ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
          {searchResults.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t("emoji.none")}</p>
          ) : (
            <div className="grid grid-cols-8">
              {searchResults.map((entry) => (
                <EmojiButton char={entry.char} key={entry.name} name={entry.name} onPick={pick} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-2 pb-1">
            {sections.map((section) => (
              <button
                aria-label={t(section.labelKey)}
                className={cn(
                  "grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
                  activeKey === section.key && "bg-accent text-foreground",
                )}
                key={section.key}
                onClick={() => scrollToSection(section.key)}
                type="button"
              >
                <section.icon className="size-4" />
              </button>
            ))}
          </div>
          <div className="relative min-h-0 flex-1 overflow-y-auto px-2 pb-2" onScroll={syncActiveCategory} ref={scrollRef}>
            {sections.map((section) => (
              <div data-emoji-section={section.key} key={section.key}>
                <div className="sticky top-0 z-10 bg-popover px-1 py-1 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">
                  {t(section.labelKey)}
                </div>
                <div className="grid grid-cols-8">
                  {section.entries.map((entry) => (
                    <EmojiButton char={entry.char} key={`${section.key}:${entry.name}`} name={entry.name} onPick={pick} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EmojiButton({ char, name, onPick }: { char: string; name: string; onPick: (char: string) => void }) {
  return (
    <button
      className="grid size-8 place-items-center rounded-md text-lg transition-colors hover:bg-accent"
      onClick={() => onPick(char)}
      title={name === char ? undefined : name.replaceAll("_", " ")}
      type="button"
    >
      {char}
    </button>
  );
}
